import { useState, useMemo } from 'react';
import {
  Container,
  Paper,
  Grid,
  TextField,
  Button,
  Typography,
  Autocomplete,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  LinearProgress,
  Box,
  Card,
  CardContent,
  Collapse,
  IconButton,
  InputAdornment,
  Chip,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RouteIcon from '@mui/icons-material/Route';
import TimerIcon from '@mui/icons-material/Timer';
import SpeedIcon from '@mui/icons-material/Speed';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PageLayout from '../common/components/PageLayout';
import ManagementMenu from './ManagementMenu';
import { useTranslation } from '../common/components/LocalizationProvider';
import { useEffectAsync } from '../reactHelper';

const useStyles = makeStyles()((theme) => ({
  container: {
    marginTop: theme.spacing(2),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
  },
  filterPaper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  statsCard: {
    height: '100%',
    textAlign: 'center',
  },
  tripRow: {
    '& > *': { borderBottom: 'unset' },
  },
  detailsBox: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.mode === 'dark'
      ? theme.palette.grey[800]
      : theme.palette.grey[100],
  },
}));

const formatDuration = (ms) => {
  const hours = Math.floor(ms / 3600000);
  const minutes = Math.floor((ms % 3600000) / 60000);
  return `${hours}h ${minutes}m`;
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString();
};

const TripRow = ({ trip, device, t }) => {
  const [open, setOpen] = useState(false);
  const { classes } = useStyles();

  return (
    <>
      <TableRow className={classes.tripRow}>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{device?.name || '-'}</TableCell>
        <TableCell>{formatDateTime(trip.startTime)}</TableCell>
        <TableCell>{formatDateTime(trip.endTime)}</TableCell>
        <TableCell align="right">{formatDuration(trip.duration)}</TableCell>
        <TableCell align="right">{(trip.distance / 1000).toFixed(2)}</TableCell>
        <TableCell align="right">{(trip.averageSpeed * 1.852).toFixed(1)}</TableCell>
        <TableCell align="right">{(trip.maxSpeed * 1.852).toFixed(1)}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box className={classes.detailsBox}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('reportStartAddress')}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {trip.startAddress || '-'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('reportEndAddress')}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {trip.endAddress || '-'}
                  </Typography>
                </Grid>
                {trip.driverName && (
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {t('sharedDriver')}
                    </Typography>
                    <Typography variant="body2">{trip.driverName}</Typography>
                  </Grid>
                )}
                {trip.fuelUsed > 0 && (
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {t('reportSpentFuel')}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography variant="body2">{trip.fuelUsed.toFixed(2)} L</Typography>
                      {trip.fuelEstimated && <Chip label="est" size="small" />}
                    </Box>
                  </Grid>
                )}
                {trip.fuelCost > 0 && (
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {t('reportFuelCost')}
                    </Typography>
                    <Typography variant="body2">R$ {trip.fuelCost.toFixed(2)}</Typography>
                  </Grid>
                )}
                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('positionOdometer')}
                  </Typography>
                  <Typography variant="body2">
                    {trip.endOdometer ? `${(trip.endOdometer / 1000).toFixed(1)} km` : '-'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const TripsAnalysisPage = () => {
  const { classes } = useStyles();
  const t = useTranslation();

  const [devices, setDevices] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [fromDate, setFromDate] = useState(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000));
  const [toDate, setToDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [trips, setTrips] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [fuelPrice, setFuelPrice] = useState(5.50);

  useEffectAsync(async () => {
    const response = await fetch('/api/devices');
    if (response.ok) {
      setDevices(await response.json());
    }
  }, []);

  const handleSearch = async () => {
    if (selectedDevices.length === 0) return;

    setLoading(true);
    setTrips([]);

    try {
      const from = fromDate.toISOString();
      const to = toDate.toISOString();

      const allTrips = [];

      for (const device of selectedDevices) {
        const response = await fetch(`/api/reports/trips?deviceId=${device.id}&from=${from}&to=${to}`, {
          headers: { Accept: 'application/json' },
        });
        if (response.ok) {
          const deviceTrips = await response.json();
          deviceTrips.forEach((trip) => {
            let tripFuel = trip.spentFuel || 0;
            let fuelEstimated = false;

            // Se não há dados de combustível do sensor, estimar pelo consumo do dispositivo
            if (tripFuel === 0 && device?.attributes?.form?.fuelConsumption) {
              const consumptionKmL = parseFloat(device.attributes.form.fuelConsumption);
              if (consumptionKmL > 0) {
                tripFuel = (trip.distance / 1000) / consumptionKmL;
                fuelEstimated = true;
              }
            }

            allTrips.push({
              ...trip,
              device,
              fuelUsed: tripFuel,
              fuelEstimated,
              fuelCost: tripFuel * fuelPrice,
            });
          });
        }
      }

      // Sort by start time descending
      allTrips.sort((a, b) => new Date(b.startTime) - new Date(a.startTime));
      setTrips(allTrips);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (trips.length === 0) return;

    const ExcelJS = (await import('exceljs')).default;
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Trips');

    worksheet.columns = [
      { header: t('deviceTitle'), key: 'deviceName', width: 25 },
      { header: t('reportStartTime'), key: 'startTime', width: 20 },
      { header: t('reportEndTime'), key: 'endTime', width: 20 },
      { header: t('reportDuration'), key: 'duration', width: 12 },
      { header: `${t('reportDistance')} (km)`, key: 'distance', width: 15 },
      { header: `${t('reportAverageSpeed')} (km/h)`, key: 'avgSpeed', width: 18 },
      { header: `${t('reportMaximumSpeed')} (km/h)`, key: 'maxSpeed', width: 18 },
      { header: `${t('reportSpentFuel')} (L)`, key: 'fuelUsed', width: 15 },
      { header: `${t('reportFuelCost')} (R$)`, key: 'fuelCost', width: 15 },
      { header: t('reportStartAddress'), key: 'startAddress', width: 35 },
      { header: t('reportEndAddress'), key: 'endAddress', width: 35 },
      { header: t('sharedDriver'), key: 'driver', width: 20 },
    ];

    trips.forEach((trip) => {
      worksheet.addRow({
        deviceName: trip.device?.name || '-',
        startTime: formatDateTime(trip.startTime),
        endTime: formatDateTime(trip.endTime),
        duration: formatDuration(trip.duration),
        distance: (trip.distance / 1000).toFixed(2),
        avgSpeed: (trip.averageSpeed * 1.852).toFixed(1),
        maxSpeed: (trip.maxSpeed * 1.852).toFixed(1),
        fuelUsed: `${trip.fuelUsed.toFixed(2)}${trip.fuelEstimated ? ' (est)' : ''}`,
        fuelCost: trip.fuelCost.toFixed(2),
        startAddress: trip.startAddress || '-',
        endAddress: trip.endAddress || '-',
        driver: trip.driverName || '-',
      });
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trips_${new Date().toISOString().split('T')[0]}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const stats = useMemo(() => {
    if (trips.length === 0) return null;
    const totalDistance = trips.reduce((acc, t) => acc + t.distance, 0) / 1000;
    const totalDuration = trips.reduce((acc, t) => acc + t.duration, 0);
    const avgSpeed = trips.reduce((acc, t) => acc + t.averageSpeed, 0) / trips.length;
    const totalFuelCost = trips.reduce((acc, t) => acc + (t.fuelCost || 0), 0);
    return {
      tripCount: trips.length,
      totalDistance: totalDistance.toFixed(1),
      totalDuration: formatDuration(totalDuration),
      avgSpeed: (avgSpeed * 1.852).toFixed(1),
      totalFuelCost: totalFuelCost.toFixed(2),
    };
  }, [trips]);

  return (
    <PageLayout menu={<ManagementMenu />} breadcrumbs={['reportManagement', 'reportTripsAnalysis']}>
      <Container maxWidth={false} className={classes.container}>
        {/* Filters */}
        <Paper className={classes.filterPaper}>
          <Typography variant="h6" gutterBottom>
            <RouteIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            {t('reportTripsAnalysis')}
          </Typography>

          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <Autocomplete
                multiple
                options={devices}
                getOptionLabel={(option) => option.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                value={selectedDevices}
                onChange={(e, value) => setSelectedDevices(value)}
                renderOption={(props, option) => {
                  const { key, ...rest } = props;
                  return (
                    <li {...rest} key={option.id}>
                      {option.name}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField {...params} label={t('deviceTitle')} size="small" />
                )}
                limitTags={2}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={t('reportFrom')}
                  value={fromDate}
                  onChange={setFromDate}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={t('reportTo')}
                  value={toDate}
                  onChange={setToDate}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <TextField
                size="small"
                type="number"
                label={t('reportFuelPrice')}
                value={fuelPrice}
                onChange={(e) => setFuelPrice(Number(e.target.value) || 0)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">R$/L</InputAdornment>,
                }}
                inputProps={{ step: 0.01, min: 0 }}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <Button
                variant="contained"
                fullWidth
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                disabled={loading || selectedDevices.length === 0}
              >
                {t('sharedSearch')}
              </Button>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<FileDownloadIcon />}
                onClick={handleExport}
                disabled={trips.length === 0}
              >
                Excel
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {/* Stats Cards */}
        {stats && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 6, md: 3 }}>
              <Card className={classes.statsCard}>
                <CardContent>
                  <RouteIcon color="primary" sx={{ fontSize: 32 }} />
                  <Typography variant="h6">{t('reportTrips')}</Typography>
                  <Typography variant="h4">{stats.tripCount}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Card className={classes.statsCard}>
                <CardContent>
                  <SpeedIcon color="info" sx={{ fontSize: 32 }} />
                  <Typography variant="h6">{t('reportTotalDistance')}</Typography>
                  <Typography variant="h5">{stats.totalDistance} km</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Card className={classes.statsCard}>
                <CardContent>
                  <TimerIcon color="warning" sx={{ fontSize: 32 }} />
                  <Typography variant="h6">{t('reportDuration')}</Typography>
                  <Typography variant="h5">{stats.totalDuration}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Card className={classes.statsCard}>
                <CardContent>
                  <AttachMoneyIcon color="success" sx={{ fontSize: 32 }} />
                  <Typography variant="h6">{t('reportTotalFuelCost')}</Typography>
                  <Typography variant="h5">R$ {stats.totalFuelCost}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Results Table */}
        {trips.length > 0 && (
          <Paper>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>{t('deviceTitle')}</TableCell>
                  <TableCell>{t('reportStartTime')}</TableCell>
                  <TableCell>{t('reportEndTime')}</TableCell>
                  <TableCell align="right">{t('reportDuration')}</TableCell>
                  <TableCell align="right">{t('reportDistance')} (km)</TableCell>
                  <TableCell align="right">{t('reportAverageSpeed')} (km/h)</TableCell>
                  <TableCell align="right">{t('reportMaximumSpeed')} (km/h)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {trips
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((trip, index) => (
                    <TripRow key={index} trip={trip} device={trip.device} t={t} />
                  ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={trips.length}
              page={page}
              onPageChange={(e, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </Paper>
        )}

        {!loading && trips.length === 0 && selectedDevices.length > 0 && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <RouteIcon sx={{ fontSize: 48, opacity: 0.5, mb: 2 }} />
            <Typography color="textSecondary">{t('sharedNoData')}</Typography>
          </Paper>
        )}
      </Container>
    </PageLayout>
  );
};

export default TripsAnalysisPage;
